import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { EarlyAccessFormData, ContactFormData } from './formSubmissions';

// Email configuration
const NOTIFICATION_EMAIL = 'amar@duran.ai';
const FROM_EMAIL = 'noreply@duran.ai';
const FROM_NAME = 'Duran - Landing Page';

// Interface for MailerSend email document
interface EmailDocument {
  to: Array<{
    email: string;
    name?: string;
  }>;
  from: {
    email: string;
    name: string;
  };
  subject: string;
  html: string;
  text: string;
  tags?: string[];
  reply_to?: {
    email: string;
    name?: string;
  };
}

// Generate HTML email template for early access requests
const generateEarlyAccessEmailHTML = (data: EarlyAccessFormData): string => {
  const submittedAt = new Date().toLocaleString();
  
  return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>New Early Access Request</title>
    <style>
        * { box-sizing: border-box; }
        body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
            line-height: 1.5; 
            margin: 0; 
            padding: 20px; 
            background-color: #0a0a0a; 
            color: #ffffff;
        }
        .container { 
            max-width: 600px; 
            margin: 0 auto; 
            background: rgba(17, 24, 39, 0.95); 
            border: 1px solid rgba(55, 65, 81, 1); 
            border-radius: 16px; 
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.6); 
            overflow: hidden;
            backdrop-filter: blur(4px);
        }
        .pill-tag {
            display: inline-block;
            background: linear-gradient(135deg, #14b8a6, #84cc16);
            color: white;
            padding: 8px 16px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin: 20px 20px 0 20px;
        }
        .header { 
            padding: 20px 20px 30px 20px; 
            text-align: center; 
        }
        .header h1 { 
            margin: 20px 0 8px 0; 
            font-size: 24px; 
            font-weight: bold; 
            color: #ffffff; 
        }
        .header p { 
            margin: 0; 
            color: #9ca3af; 
            font-size: 16px; 
        }
        .content { padding: 0 20px 20px 20px; }
        .form-grid { display: block; margin-bottom: 20px; }
        .field-group { margin-bottom: 16px; }
        .field-label { 
            display: block; 
            font-size: 14px; 
            font-weight: 500; 
            color: #d1d5db; 
            margin-bottom: 8px; 
        }
        .field-label .icon { 
            display: inline-block; 
            width: 16px; 
            height: 16px; 
            margin-right: 8px; 
            vertical-align: middle; 
        }
        .field-value { 
            width: 100%; 
            padding: 12px 16px; 
            background-color: #1f2937; 
            border: 1px solid #374151; 
            border-radius: 8px; 
            color: #ffffff; 
            font-size: 16px;
            word-break: break-word;
        }
        .field-value.large { 
            min-height: 80px; 
            white-space: pre-line; 
        }
        .field-value a { 
            color: #14b8a6; 
            text-decoration: none; 
        }
        .metadata { 
            background-color: rgba(31, 41, 55, 0.5); 
            border: 1px solid #374151; 
            border-radius: 8px; 
            padding: 16px; 
            margin-top: 20px; 
        }
        .metadata h3 { 
            margin: 0 0 12px 0; 
            color: #ffffff; 
            font-size: 14px; 
            font-weight: 600; 
        }
        .metadata-item { 
            display: flex; 
            justify-content: space-between; 
            margin-bottom: 8px; 
            font-size: 14px; 
            color: #d1d5db; 
        }
        .metadata-item:last-child { margin-bottom: 0; }
        .footer { 
            padding: 16px 20px; 
            text-align: center; 
            color: #6b7280; 
            font-size: 12px; 
            border-top: 1px solid #374151; 
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="pill-tag">🚀 Early Access Request</div>
        
        <div class="header">
            <h1>New Early Access Request</h1>
            <p>Someone wants early access to Duran AI!</p>
        </div>
        
        <div class="content">
            <div class="form-grid">
                <div class="field-group">
                    <label class="field-label">
                        <span class="icon">👤</span>Full Name
                    </label>
                    <div class="field-value">${data.name}</div>
                </div>
                
                <div class="field-group">
                    <label class="field-label">
                        <span class="icon">📧</span>Email Address
                    </label>
                    <div class="field-value"><a href="mailto:${data.email}">${data.email}</a></div>
                </div>
                
                <div class="field-group">
                    <label class="field-label">
                        <span class="icon">🏢</span>Company
                    </label>
                    <div class="field-value">${data.company}</div>
                </div>
                
                <div class="field-group">
                    <label class="field-label">
                        <span class="icon">💡</span>Use Case
                    </label>
                    <div class="field-value large">${data.useCase}</div>
                </div>
            </div>
            
            <div class="metadata">
                <h3>📊 Submission Details</h3>
                <div class="metadata-item">
                    <span><strong>Submitted:</strong></span>
                    <span>${submittedAt}</span>
                </div>
                <div class="metadata-item">
                    <span><strong>Language:</strong></span>
                    <span>${data.language || 'en'}</span>
                </div>
                <div class="metadata-item">
                    <span><strong>Referrer:</strong></span>
                    <span>${data.referrer || 'direct'}</span>
                </div>
                <div class="metadata-item">
                    <span><strong>User Agent:</strong></span>
                    <span style="font-size: 12px; opacity: 0.8;">${data.userAgent?.substring(0, 60)}...</span>
                </div>
            </div>
        </div>
        
        <div class="footer">
            <p>This notification was sent from the Duran AI landing page early access form.</p>
        </div>
    </div>
</body>
</html>`;
};

// Generate text version for early access emails
const generateEarlyAccessEmailText = (data: EarlyAccessFormData): string => {
  const submittedAt = new Date().toLocaleString();
  
  return `
NEW EARLY ACCESS REQUEST - DURAN

👤 Name: ${data.name}
📧 Email: ${data.email}  
🏢 Company: ${data.company}

💡 Use Case:
${data.useCase}

📊 Submission Details:
- Submitted: ${submittedAt}
- Language: ${data.language || 'en'}
- Referrer: ${data.referrer || 'direct'}
- User Agent: ${data.userAgent?.substring(0, 100)}...

This notification was sent from the Duran landing page early access form.
`;
};

// Generate HTML email template for contact requests
const generateContactEmailHTML = (data: ContactFormData): string => {
  const submittedAt = new Date().toLocaleString();
  
  return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>New Contact Request</title>
    <style>
        * { box-sizing: border-box; }
        body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
            line-height: 1.5; 
            margin: 0; 
            padding: 20px; 
            background-color: #0a0a0a; 
            color: #ffffff;
        }
        .container { 
            max-width: 600px; 
            margin: 0 auto; 
            background: rgba(17, 24, 39, 0.95); 
            border: 1px solid rgba(55, 65, 81, 1); 
            border-radius: 16px; 
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.6); 
            overflow: hidden;
            backdrop-filter: blur(4px);
        }
        .pill-tag {
            display: inline-block;
            background: linear-gradient(135deg, #3b82f6, #8b5cf6);
            color: white;
            padding: 8px 16px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin: 20px 20px 0 20px;
        }
        .header { 
            padding: 20px 20px 30px 20px; 
            text-align: center; 
        }
        .header h1 { 
            margin: 20px 0 8px 0; 
            font-size: 24px; 
            font-weight: bold; 
            color: #ffffff; 
        }
        .header p { 
            margin: 0; 
            color: #9ca3af; 
            font-size: 16px; 
        }
        .content { padding: 0 20px 20px 20px; }
        .form-grid { display: block; margin-bottom: 20px; }
        .field-group { margin-bottom: 16px; }
        .field-label { 
            display: block; 
            font-size: 14px; 
            font-weight: 500; 
            color: #d1d5db; 
            margin-bottom: 8px; 
        }
        .field-label .icon { 
            display: inline-block; 
            width: 16px; 
            height: 16px; 
            margin-right: 8px; 
            vertical-align: middle; 
        }
        .field-value { 
            width: 100%; 
            padding: 12px 16px; 
            background-color: #1f2937; 
            border: 1px solid #374151; 
            border-radius: 8px; 
            color: #ffffff; 
            font-size: 16px;
            word-break: break-word;
        }
        .field-value.large { 
            min-height: 80px; 
            white-space: pre-line; 
        }
        .field-value a { 
            color: #3b82f6; 
            text-decoration: none; 
        }
        .metadata { 
            background-color: rgba(31, 41, 55, 0.5); 
            border: 1px solid #374151; 
            border-radius: 8px; 
            padding: 16px; 
            margin-top: 20px; 
        }
        .metadata h3 { 
            margin: 0 0 12px 0; 
            color: #ffffff; 
            font-size: 14px; 
            font-weight: 600; 
        }
        .metadata-item { 
            display: flex; 
            justify-content: space-between; 
            margin-bottom: 8px; 
            font-size: 14px; 
            color: #d1d5db; 
        }
        .metadata-item:last-child { margin-bottom: 0; }
        .footer { 
            padding: 16px 20px; 
            text-align: center; 
            color: #6b7280; 
            font-size: 12px; 
            border-top: 1px solid #374151; 
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="pill-tag">📞 Contact Form</div>
        
        <div class="header">
            <h1>New Contact Request</h1>
            <p>Someone wants to get in touch!</p>
        </div>
        
        <div class="content">
            <div class="form-grid">
                <div class="field-group">
                    <label class="field-label">
                        <span class="icon">👤</span>Full Name
                    </label>
                    <div class="field-value">${data.name}</div>
                </div>
                
                <div class="field-group">
                    <label class="field-label">
                        <span class="icon">📧</span>Email Address
                    </label>
                    <div class="field-value"><a href="mailto:${data.email}">${data.email}</a></div>
                </div>
                
                <div class="field-group">
                    <label class="field-label">
                        <span class="icon">🏢</span>Company
                    </label>
                    <div class="field-value">${data.company}</div>
                </div>
                
                <div class="field-group">
                    <label class="field-label">
                        <span class="icon">💬</span>Message
                    </label>
                    <div class="field-value large">${data.useCase}</div>
                </div>
            </div>
            
            <div class="metadata">
                <h3>📊 Submission Details</h3>
                <div class="metadata-item">
                    <span><strong>Submitted:</strong></span>
                    <span>${submittedAt}</span>
                </div>
                <div class="metadata-item">
                    <span><strong>Language:</strong></span>
                    <span>${data.language || 'en'}</span>
                </div>
                <div class="metadata-item">
                    <span><strong>Referrer:</strong></span>
                    <span>${data.referrer || 'direct'}</span>
                </div>
                <div class="metadata-item">
                    <span><strong>User Agent:</strong></span>
                    <span style="font-size: 12px; opacity: 0.8;">${data.userAgent?.substring(0, 60)}...</span>
                </div>
            </div>
        </div>
        
        <div class="footer">
            <p>This notification was sent from the Duran AI landing page contact form.</p>
        </div>
    </div>
</body>
</html>`;
};

// Generate text version for contact emails
const generateContactEmailText = (data: ContactFormData): string => {
  const submittedAt = new Date().toLocaleString();
  
  return `
NEW CONTACT REQUEST - DURAN

👤 Name: ${data.name}
📧 Email: ${data.email}  
🏢 Company: ${data.company}

💬 Message:
${data.useCase}

📊 Submission Details:
- Submitted: ${submittedAt}
- Language: ${data.language || 'en'}
- Referrer: ${data.referrer || 'direct'}
- User Agent: ${data.userAgent?.substring(0, 100)}...

This notification was sent from the Duran landing page contact form.
`;
};

// Send early access notification email
export const sendEarlyAccessNotification = async (data: EarlyAccessFormData): Promise<string> => {
  try {
    const emailDoc: EmailDocument = {
      to: [
        {
          email: NOTIFICATION_EMAIL,
          name: 'Duran Team'
        }
      ],
      from: {
        email: FROM_EMAIL,
        name: FROM_NAME
      },
      subject: `🚀 New Early Access Request from ${data.name} (${data.company})`,
      html: generateEarlyAccessEmailHTML(data),
      text: generateEarlyAccessEmailText(data),
      tags: ['early-access', 'notification', 'landing-page'],
      reply_to: {
        email: data.email,
        name: data.name
      }
    };

    const docRef = await addDoc(collection(db, 'emails'), {
      ...emailDoc,
      createdAt: serverTimestamp(),
      processed: false,
      source: 'early-access-form'
    });
    
    console.log('Early access notification email queued with ID: ', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error sending early access notification: ', error);
    throw new Error('Failed to send notification email');
  }
};

// Send contact form notification email  
export const sendContactNotification = async (data: ContactFormData): Promise<string> => {
  try {
    const emailDoc: EmailDocument = {
      to: [
        {
          email: NOTIFICATION_EMAIL,
          name: 'Duran Team'
        }
      ],
      from: {
        email: FROM_EMAIL,
        name: FROM_NAME
      },
      subject: `📞 New Contact Request from ${data.name} (${data.company})`,
      html: generateContactEmailHTML(data),
      text: generateContactEmailText(data),
      tags: ['contact', 'notification', 'landing-page'],
      reply_to: {
        email: data.email,
        name: data.name
      }
    };

    const docRef = await addDoc(collection(db, 'emails'), {
      ...emailDoc,
      createdAt: serverTimestamp(),
      processed: false,
      source: 'contact-form'
    });
    
    console.log('Contact notification email queued with ID: ', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error sending contact notification: ', error);
    throw new Error('Failed to send notification email');
  }
};